import { useEffect, useState } from "react";

const MOBILE_BREAKPOINT = 768;

const MOBILE_QUERY =
  `(max-width: ${MOBILE_BREAKPOINT - 1}px), (max-height: ${MOBILE_BREAKPOINT - 1}px)`;

/**
 * Reactive media query for the mobile/desktop split.
 *
 * Matches when the viewport's *shorter* side is under Tailwind's `md`
 * breakpoint, so a phone stays on the mobile layout in both orientations.
 * A width-only test flipped to desktop on rotation, which swapped the routed
 * component and unmounted whatever the user had open. Returns `false` during
 * the brief pre-mount window so the desktop layout is the default — avoids a
 * server-render mismatch and keeps SSR/hydration safe.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}
